/**
 * 媒体总库页面 — MediaPool（挑媒体工具，借鉴软文街筛选体验）。
 *
 * 界面目标：租户企业从全量媒体资源中「按条件圈定 → 勾选 → 批量加入投放库」。
 * 结构：4 概览指标 → 高级筛选工具条（下拉+价格区间+自媒体+排序）→
 *       批量操作栏（已选 N 项，批量加入投放库）→ 媒体列表表（白名单指标列）。
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Grid,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import ChipMultiSelect from "../components/ChipMultiSelect";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import {
  getMediaPool,
  getMediaPoolOverview,
  getMediaPoolFilters,
  refreshMediaPool,
  batchAddPoolToDelivery,
  type MediaPoolParams,
} from "../api/mediaPool";
import { mediaTierBadgeConfig } from "../utils/constants";
import { useCompanyStore } from "../store/companyStore";
import type { MediaPoolItem, MediaPoolOverview as OverviewData, MediaPoolFilters } from "../types";

/** 媒体等级徽章。 */
function TierBadge({ tier }: { tier?: string | null }) {
  const info = mediaTierBadgeConfig(tier);
  return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
}

/** 数字指标值（月访问量/百度收录等），空/非法显示“—”，避免 NaN。 */
function MetricValue({ value }: { value?: string | number | null }) {
  if (value == null || value === "") return <Typography sx={{ fontSize: 11, color: "text.secondary" }}>—</Typography>;
  const n = Number(value);
  const text = Number.isFinite(n) ? n.toLocaleString() : String(value);
  return <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{text}</Typography>;
}

/** 排序选项。 */
const SORT_OPTIONS = [
  { value: "", label: "默认排序" },
  { value: "price", label: "按价格" },
  { value: "sort_type", label: "按媒体等级" },
  { value: "success_radio", label: "按成功率" },
  { value: "name", label: "按媒体名" },
  { value: "parent_site", label: "按站点" },
];

export default function MediaPool() {
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [filters, setFilters] = useState<MediaPoolFilters | null>(null);

  // 当前选中品牌/产品（加入投放库时绑定，投放库按品牌隔离，与引用源追溯一致）
  const selectedBrandId = useCompanyStore((s) => s.selectedBrandId);
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);

  const [items, setItems] = useState<MediaPoolItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [snack, setSnack] = useState("");

  // 筛选条件
  const [filterKeyword, setFilterKeyword] = useState("");
  const [filterParent, setFilterParent] = useState("");
  const [filterTaxonomy, setFilterTaxonomy] = useState<string[]>([]);
  const [filterArea, setFilterArea] = useState("");
  const [filterPlatform, setFilterPlatform] = useState<string[]>([]);
  const [filterTier, setFilterTier] = useState("");
  const [filterZimeiti, setFilterZimeiti] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [orderDir, setOrderDir] = useState("desc");

  // 勾选
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitBatch, setSubmitBatch] = useState(false);

  /** 加载概览 + 筛选下拉。
   *
   * 注意：媒体总库是【平台级共享资源】，所有企业看到的数据完全一致（9.3万条），
   * 不随 selectedCompanyId 变化，因此这里 **不依赖 selectedCompanyId**（仅挂载时加载一次）。
   *（投放库 media_delivery 才按企业隔离，切换企业需刷新 —— 见 MediaInsight。）
   */
  useEffect(() => {
    getMediaPoolOverview()
      .then(setOverview)
      .catch(() => setOverview(null))
      .finally(() => setOverviewLoading(false));
    getMediaPoolFilters()
      .then(setFilters)
      .catch(() => setFilters(null));
  }, []);

  /** 加载列表。 */
  const fetchList = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const params: MediaPoolParams = {
        page: page + 1,
        size,
        keyword: filterKeyword || undefined,
        parent_site: filterParent || undefined,
        taxonomy: filterTaxonomy.length ? filterTaxonomy.join(",") : undefined,
        area: filterArea || undefined,
        platform: filterPlatform.length ? filterPlatform.join(",") : undefined,
        sort_type: filterTier || undefined,
        is_zimeiti: filterZimeiti ? Number(filterZimeiti) : undefined,
        price_min: priceMin ? Number(priceMin) : undefined,
        price_max: priceMax ? Number(priceMax) : undefined,
        order_by: orderBy || undefined,
        order_dir: orderBy ? orderDir : undefined,
      };
      const res = await getMediaPool(params);
      setItems(res.items || []);
      setTotal(res.total || 0);
      setSelected(new Set());
    } catch {
      setItems([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [page, size, filterKeyword, filterParent, filterTaxonomy, filterArea, filterPlatform, filterTier, filterZimeiti, priceMin, priceMax, orderBy, orderDir]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const flash = (msg: string) => {
    setSnack(msg);
    window.setTimeout(() => setSnack(""), 3000);
  };

  /** 触发同步。 */
  const handleRefresh = async () => {
    setSyncing(true);
    try {
      const res = await refreshMediaPool(5);
      if (res.error) {
        flash(res.error);
      } else {
        flash(`同步完成：拉取 ${res.fetched} 条，入库 ${res.upserted} 条`);
        fetchList();
        getMediaPoolOverview()
          .then(setOverview)
          .catch(() => setOverview(null));
      }
    } catch (e) {
      flash(e instanceof Error ? e.message : "同步失败");
    } finally {
      setSyncing(false);
    }
  };

  /** 勾选/取消勾选。 */
  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** 全选当前页。 */
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (items.every((i) => next.has(i.id))) {
        items.forEach((i) => next.delete(i.id));
      } else {
        items.forEach((i) => next.add(i.id));
      }
      return next;
    });
  };

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  /** 批量加入投放库（绑定当前品牌/产品）。 */
  const handleBatchAdd = async () => {
    if (selectedIds.length === 0) return;
    if (!selectedBrandId) {
      flash("请先在顶栏选择品牌，再加入投放库");
      return;
    }
    setSubmitBatch(true);
    try {
      const res = await batchAddPoolToDelivery(selectedIds, "manual", {
        brand_id: selectedBrandId,
        product_id: selectedProductId || undefined,
      });
      const msg = [
        res.added.length ? `加入 ${res.added.length} 家` : "",
        res.duplicated.length ? `已在投放库 ${res.duplicated.length} 家` : "",
      ].filter(Boolean).join("，");
      flash(msg || "未新增");
      setSelected(new Set());
      fetchList();
    } catch (e) {
      flash(e instanceof Error ? e.message : "批量加入失败");
    } finally {
      setSubmitBatch(false);
    }
  };

  const columns: DataTableColumn<MediaPoolItem>[] = [
    {
      key: "select",
      label: (
        <Checkbox
          size="small"
          checked={items.length > 0 && items.every((i) => selected.has(i.id))}
          onChange={toggleSelectAll}
        />
      ),
      render: (row) => (
        <Checkbox size="small" checked={selected.has(row.id)} onChange={() => toggleSelect(row.id)} />
      ),
    },
    {
      key: "name",
      label: "媒体名称",
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{row.name}</Typography>
          {row.is_recommend && <Badge label="推荐" variant="info" />}
        </Box>
      ),
    },
    {
      key: "parent_site",
      label: "平台/站点",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.parent_site || row.platform || "—"}
        </Typography>
      ),
    },
    {
      key: "taxonomy",
      label: "行业频道",
      render: (row) => <Typography sx={{ fontSize: 12 }}>{row.taxonomy || "—"}</Typography>,
    },
    {
      key: "area",
      label: "地区",
      render: (row) => <Typography sx={{ fontSize: 12 }}>{row.area || "—"}</Typography>,
    },
    {
      key: "tier",
      label: "媒体等级",
      align: "center",
      render: (row) => <TierBadge tier={row.sort_type} />,
    },
    {
      key: "price",
      label: "价格",
      align: "right",
      render: (row) => (
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {row.price != null ? `¥${row.price.toLocaleString()}` : "—"}
        </Typography>
      ),
    },
    {
      key: "radio",
      label: "成功/收录率",
      align: "center",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.success_radio || "—"} / {row.include_radio || "—"}
        </Typography>
      ),
    },
    {
      key: "visits",
      label: "月访问量",
      align: "right",
      render: (row) => <MetricValue value={row.monthly_visits} />,
    },
    {
      key: "baidu",
      label: "百度收录",
      align: "right",
      render: (row) => <MetricValue value={row.baidu_s_count_all} />,
    },
    {
      key: "domain",
      label: "域名年龄/ICP",
      render: (row) => (
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          {row.domain_age ? `${row.domain_age}·${row.icp_nature_name || ""}` : "—"}
        </Typography>
      ),
    },
    {
      key: "action",
      label: "操作",
      align: "center",
      render: (row) => (
        <Button
          size="small"
          variant="outlined"
          sx={{ whiteSpace: "nowrap", py: 0.25 }}
          onClick={() => toggleSelect(row.id)}
        >
          {selected.has(row.id) ? "已选" : "挑选"}
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="媒体总库"
        breadcrumbs={[{ label: "媒体投放" }, { label: "媒体总库" }]}
        subtitle="全量媒体资源 · 按条件圈定 → 勾选加入投放库（借鉴软文街筛选体验）"
        actions={
          <Button variant="contained" size="small" onClick={handleRefresh} disabled={syncing}>
            {syncing ? "同步中..." : "同步媒体资源"}
          </Button>
        }
      />

      {/* 概览指标卡 ×4（overviewLoading 时用骨架，避免显示“—”误导） */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {overviewLoading || !overview ? (
          <>
            {[0, 1, 2, 3].map((i) => (
              <Grid item key={i} xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Skeleton width="60%" height={20} />
                    <Skeleton width="40%" height={36} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </>
        ) : (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="媒体资源总数" value={overview.total_media.toLocaleString()} subtitle="平台媒体资源全量镜像" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="三级以上媒体" value={overview.high_tier_count} subtitle="置顶/一级/二级媒体" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="覆盖站点" value={overview.cover_site_count} subtitle="站点归属去重" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="行业频道数" value={overview.taxonomy_count} subtitle="行业频道去重" />
            </Grid>
          </>
        )}
      </Grid>

      {/* 高级筛选 */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            label="关键词"
            value={filterKeyword}
            onChange={(e) => setFilterKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setPage(0)}
            sx={{ width: 150 }}
          />
          <ChipMultiSelect
            label="行业频道"
            options={filters?.taxonomies || []}
            value={filterTaxonomy}
            onChange={setFilterTaxonomy}
            collapseLimit={20}
            loading={!filters}
          />
          <TextField
            select
            size="small"
            label="地区"
            value={filterArea}
            onChange={(e) => setFilterArea(e.target.value)}
            sx={{ width: 120 }}
          >
            <MenuItem value="">全部</MenuItem>
            {(filters?.areas || []).map((v) => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </TextField>
          <ChipMultiSelect
            label="平台/站点"
            options={filters?.platforms || []}
            value={filterPlatform}
            onChange={setFilterPlatform}
            searchable
            collapseLimit={20}
            loading={!filters}
          />
          <TextField
            select
            size="small"
            label="媒体等级"
            value={filterTier}
            onChange={(e) => setFilterTier(e.target.value)}
            sx={{ width: 120 }}
          >
            <MenuItem value="">全部</MenuItem>
            {(filters?.sort_types || []).map((v) => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="是否自媒体"
            value={filterZimeiti}
            onChange={(e) => setFilterZimeiti(e.target.value)}
            sx={{ width: 110 }}
          >
            <MenuItem value="">全部</MenuItem>
            <MenuItem value="2">权威/软文媒体</MenuItem>
            <MenuItem value="1">自媒体</MenuItem>
          </TextField>
          <TextField
            size="small"
            type="number"
            label="价格≥"
            value={priceMin}
            onChange={(e) => setPriceMin(e.target.value)}
            sx={{ width: 90 }}
          />
          <TextField
            size="small"
            type="number"
            label="价格≤"
            value={priceMax}
            onChange={(e) => setPriceMax(e.target.value)}
            sx={{ width: 90 }}
          />
          <TextField
            select
            size="small"
            label="排序"
            value={orderBy}
            onChange={(e) => setOrderBy(e.target.value)}
            sx={{ width: 120 }}
          >
            {SORT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          {orderBy && (
            <TextField
              select
              size="small"
              label="方向"
              value={orderDir}
              onChange={(e) => setOrderDir(e.target.value)}
              sx={{ width: 90 }}
            >
              <MenuItem value="desc">降序</MenuItem>
              <MenuItem value="asc">升序</MenuItem>
            </TextField>
          )}
          <Button size="small" variant="outlined" onClick={() => setPage(0)}>
            查询
          </Button>
          <Button size="small" variant="text" onClick={() => {
            setFilterKeyword(""); setFilterParent(""); setFilterTaxonomy([]); setFilterArea("");
            setFilterPlatform([]); setFilterTier(""); setFilterZimeiti(""); setPriceMin("");
            setPriceMax(""); setOrderBy(""); setOrderDir("desc"); setPage(0);
          }}>
            重置
          </Button>
        </CardContent>
      </Card>

      {/* 批量操作栏 */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>媒体资源</Typography>
        {loading ? <Skeleton width={72} height={20} /> : <Badge label={`共 ${total} 条`} variant="info" />}
        {selected.size > 0 && (
          <>
            <Typography sx={{ fontSize: 13, color: "primary.main" }}>
              已选 {selected.size} 项
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={submitBatch}
              onClick={handleBatchAdd}
            >
              {submitBatch ? "加入中..." : "批量加入投放库"}
            </Button>
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {snack && <Typography sx={{ fontSize: 12, color: "success.main" }}>{snack}</Typography>}
      </Box>

      {/* 媒体列表 */}
      <Box>
        {loading ? (
          <Loading fullPage />
        ) : failed ? (
          <Card>
            <CardContent>
              <EmptyState text="媒体资源暂未接入" source="需先同步媒体资源" />
            </CardContent>
          </Card>
        ) : (
          <DataTable
            columns={columns}
            data={items}
            total={total}
            page={page}
            rowsPerPage={size}
            onPageChange={setPage}
            onRowsPerPageChange={(s) => {
              setSize(s);
              setPage(0);
            }}
            getRowId={(row) => row.id}
            emptyText="暂无媒体资源，请先同步"
          />
        )}
      </Box>
    </Box>
  );
}
